/* global axios */
import ApiClient from '../ApiClient';
import StreamingService from '../services/StreamingService';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  saveRecommendations({
    conversationId,
    iqRecommendation,
    eqRecommendation,
    messageId,
    sentimentScores,
  }) {
    return axios.post(`${this.url}/${conversationId}/recommendations`, {
      iq_recommendation: iqRecommendation,
      eq_recommendation: eqRecommendation,
      eqiq_msg_id: messageId,
      sentiment_scores: sentimentScores,
    });
  }

  getRecommendations(conversationId) {
    return axios.get(`${this.url}/${conversationId}/recommendations`);
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
      },
    });
  }

  getConversation(conversationId) {
    return axios.get(`${this.url}/${conversationId}`);
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(
      `${this.url}/${conversationId}/assignments?assignee_id=${agentId}`,
      {}
    );
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  // eslint-disable-next-line class-methods-use-this
  getIqRecommendations({ query, apiBaseUrl, customAttributes = {} }) {
    const {
      channel,
      dcc_auth_token: authorizationToken,
      source_cid: conversation_id,
      agent,
      user_id,
    } = customAttributes;

    let endpoint = '';
    let data = {};

    if (agent === 'langgraph') {
      endpoint = '/chat/stream';
      data = {
        input: {
          messages: [
            { role: 'user', content: [{ type: 'text', text: query }] },
          ],
        },
        channel: 'acc',
        jwt_token: authorizationToken,
      };
      if (conversation_id) {
        data.thread_id = conversation_id;
      }
    } else {
      endpoint =
        channel === 'voice' ? '/v1/chat-messages' : '/api/chat-messages';

      data = {
        channel: 'acc',
        conversation_id,
        files: [],
        inputs: {},
        parent_message_id: null,
        query,
        response_mode: 'streaming',
        jwt_token: authorizationToken,
      };

      if (user_id) {
        data.user = user_id;
      }
    }
    const fullUrl = `${apiBaseUrl}${endpoint}`;

    return StreamingService.streamRequest({
      url: fullUrl,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-virtual-host': new URL(apiBaseUrl).hostname,
        Authorization: `Bearer ${authorizationToken}`,
      },
      data,
      requestId: `iq-recommendations-${conversation_id}`,
    });
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({ inboxId, status, assigneeType, labels, teamId, conversationType }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }

  generateWorkflow(conversationId) {
    return axios.post(`${this.url}/${conversationId}/workflow`);
  }

  // Voice Call API Methods
  joinVoiceCall(conversationId) {
    return axios.post(
      `${this.url}/${conversationId}/voice_escalation/join_call`
    );
  }

  endVoiceCall(conversationId) {
    return axios.post(
      `${this.url}/${conversationId}/voice_escalation/end_call`
    );
  }

  declineVoiceCall(conversationId) {
    return axios.post(
      `${this.url}/${conversationId}/voice_escalation/decline_call`
    );
  }
}

export default new ConversationApi();
